import { motion } from "framer-motion";
import { X, MapPin, RotateCcw, Send } from "lucide-react";
import { statusLabel, statusColor } from "../lib/orderStatus";

const euro = (n) => `€${Number(n).toFixed(2).replace(".", ",")}`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (!src ? null : src.startsWith("http") ? src : `${BACKEND}${src}`);

/* Dettaglio completo di un ordine (lato cliente): prodotti, indirizzo, chat, riordina */
export const OrderDetailModal = ({ order, onClose, productsMap, onOpenProduct, onReorder, chat }) => {
  const items = order.items || [];
  const addr = order.address;
  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-ink/40 backdrop-blur-sm" onClick={onClose} data-testid="order-detail-overlay">
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="safe-pt safe-pb flex h-full w-full max-w-lg flex-col overflow-y-auto bg-cream p-6"
        data-testid="order-detail-modal"
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-overline text-ink-soft">Dettaglio ordine</p>
            <h2 className="mt-1 font-serif text-2xl text-ink">
              {new Date(order.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColor[order.status] || "bg-white/10 text-ink-soft border border-white/20"}`} data-testid="detail-status">
                {statusLabel[order.status] || order.status}
              </span>
              {order.plan === "box_mensile" && <span className="rounded-full bg-electric/15 px-2.5 py-0.5 text-xs font-medium text-electric">Box Mensile</span>}
              {order.refunded && <span className="rounded-full bg-amber/15 px-2.5 py-0.5 text-xs font-medium text-amber">Rimborsato</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="detail-close" aria-label="Chiudi dettaglio" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink hover:bg-ink/10">
            <X className="h-5 w-5" strokeWidth={1.6} />
          </button>
        </div>

        <p className="mb-2 text-xs uppercase tracking-overline text-ink-soft">Prodotti ({items.reduce((s, i) => s + i.qty, 0)})</p>
        <div className="mb-5 space-y-1.5" data-testid="detail-items">
          {items.map((i, idx) => {
            const p = productsMap?.[i.id];
            return (
              <button key={idx} onClick={() => onOpenProduct?.(i.id)} data-testid={`detail-item-${idx}`} className="flex w-full items-center gap-3 rounded-xl border border-ink/10 bg-panel p-2.5 text-left transition-colors hover:border-sage/40">
                {p?.image ? (
                  <img src={resolveImg(p.image)} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
                ) : (
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-cream text-[10px] text-ink-soft">—</span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">{i.name}</span>
                  <span className="text-xs text-ink-soft">quantità ×{i.qty}</span>
                </span>
                <span className="shrink-0 text-sm text-ink">{euro(i.price * i.qty)}</span>
              </button>
            );
          })}
        </div>

        <div className="mb-5 flex justify-between rounded-xl border border-ink/10 bg-panel px-4 py-3 text-base font-semibold text-ink">
          <span>Totale pagato</span>
          <span data-testid="detail-total">{euro(order.total)}</span>
        </div>

        {addr?.via && (
          <div className="mb-5 flex items-start gap-3 rounded-xl border border-ink/10 bg-panel px-4 py-3" data-testid="detail-address">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={1.8} />
            <p className="text-sm text-ink">
              {addr.via}{addr.civico ? ` ${addr.civico}` : ""}, {addr.cap} {addr.citta}{addr.provincia ? ` (${addr.provincia})` : ""}
              {addr.telefono && <span className="block text-xs text-ink-soft">Tel. {addr.telefono}</span>}
            </p>
          </div>
        )}
        {order.note && <p className="mb-5 rounded-xl border border-ink/10 bg-panel px-4 py-3 text-sm text-ink-soft" data-testid="detail-note">Nota: {order.note}</p>}

        <p className="mb-2 text-xs uppercase tracking-overline text-ink-soft">Messaggi con il team NEED</p>
        <div className="mb-3 max-h-60 space-y-2 overflow-y-auto rounded-xl border border-ink/10 bg-panel p-3" data-testid="detail-messages">
          {(order.messages || []).length === 0 && (
            <p className="py-1 text-center text-xs text-ink-soft">Nessun messaggio per questo ordine.</p>
          )}
          {(order.messages || []).map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${m.author === "customer" ? "ml-auto rounded-br-sm bg-ink text-cream" : "rounded-bl-sm bg-terra/10 text-ink"}`}>
              <p>{m.text}</p>
              <p className={`mt-0.5 text-[10px] uppercase tracking-wide ${m.author === "customer" ? "text-cream/60" : "text-terra"}`}>
                {m.author === "customer" ? "Tu" : "Team NEED"} · {new Date(m.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} {new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          ))}
        </div>
        <div className="mb-5 flex gap-2">
          <input
            value={chat.chatText}
            onChange={(e) => chat.setChatText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && chat.onSend(order.id)}
            placeholder="Scrivi un messaggio sull'ordine…"
            data-testid="detail-chat-input"
            className="flex-1 rounded-full border border-ink/15 bg-panel px-5 py-2.5 text-sm outline-none focus:border-sage"
          />
          <button onClick={() => chat.onSend(order.id)} disabled={chat.chatSending || !chat.chatText.trim()} data-testid="detail-chat-send" className="grid h-10 w-10 place-items-center rounded-full bg-sage text-cream hover:bg-sage-deep disabled:opacity-50">
            <Send className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <button onClick={() => { onReorder?.(order); onClose(); }} data-testid="detail-reorder" className="mt-auto flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-sm font-semibold text-cream hover:bg-sage">
          <RotateCcw className="h-4 w-4" /> Riordina questi prodotti
        </button>
      </motion.div>
    </div>
  );
};
